import { useRef, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router";
import CountryPhoneInput from "@/customComponents/authComponents/CountryPhoneInput";
import { isValidPhoneNumber } from "libphonenumber-js";
import { authService } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import { consumeRedirectAfterLogin } from "@/utils/functions";
import SocialLoginButtons from "@/customComponents/authComponents/SocialLoginButtons";
import AuthLayout from "./AuthLayout";
import { otpFromResponse } from "@/utils/otpFromResponse";

export default function SignUp() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const wasAuthenticatedOnMount = useRef(isAuthenticated);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect only if user was already logged in when landing here
  // (avoid racing with imperative navigate() in social/OTP login flows)
  if (wasAuthenticatedOnMount.current) {
    return <Navigate to={consumeRedirectAfterLogin() || "/"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!firstName.trim()) { setError("First name is required"); return; }
    if (!lastName.trim()) { setError("Last name is required"); return; }
    if (!email.trim()) { setError("Email is required"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Email is not valid"); return; }
    if (!phoneNumber) { setError("Phone number is required"); return; }
    if (!isValidPhoneNumber(phoneNumber)) { setError("Phone number is not valid"); return; }

    setLoading(true);
    const result = await authService.register({
      firstName,
      lastName,
      email,
      phone: phoneNumber,
    });
    setLoading(false);

    if (result.success) {
      // Test accounts come back with the code attached — see `utils/otpFromResponse`.
      navigate("/otp-verification", {
        state: { otpMethod: "email", identifier: email, prefill: otpFromResponse(result.data) },
      });
    } else {
      setError(result.message);
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar">
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <div>
            <div className="mb-5 sm:mb-8">
              <h1 className="mb-2 font-semibold text-gray-800 text-2xl sm:text-3xl text-center">
                Register
              </h1>
            </div>
            <div>
              {/* Social login FIRST — directly under the heading, above the form. Matches
                  the booking portal and SignIn: Google and Apple are the fastest way to
                  register, so they lead the screen. */}
              <SocialLoginButtons disabled={loading} large />

              {/* or */}
              <div className="relative py-3 sm:py-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 py-1 text-gray-800 bg-emerald-200 rounded-full">
                    or
                  </span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="space-y-5">
                  {/* First name / Last name */}
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <input
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => { setFirstName(e.target.value); setError(""); }}
                        className="h-14 w-full rounded-full border border-gray-300 bg-transparent px-6 text-sm text-gray-800 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => { setLastName(e.target.value); setError(""); }}
                        className="h-14 w-full rounded-full border border-gray-300 bg-transparent px-6 text-sm text-gray-800 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Email / Phone */}
                  <div className="grid grid-cols-1 gap-5">
                    <div>
                      <input
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(""); }}
                        className="h-14 w-full rounded-full border border-gray-300 bg-transparent px-6 text-sm text-gray-800 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                    <div className="auth-phone-input">
                      <CountryPhoneInput
                        defaultCountry="ca"
                        value={phoneNumber}
                        onChange={(phone) => { setPhoneNumber(phone); setError(""); }}
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-red-500 text-center">{error}</p>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative flex items-center justify-center w-full h-14 rounded-full bg-gray-900 text-white text-sm font-medium transition hover:bg-gray-800 disabled:opacity-60"
                    >
                      {loading ? "Registering..." : "Continue"}
                      <span className="absolute right-2 flex items-center justify-center w-10 h-10 rounded-full bg-emerald-300">
                        <svg className="size-5 text-gray-900" viewBox="0 0 20 20" fill="none">
                          <path d="M4.167 10h11.666M10.833 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              </form>

              {/* Or divider */}
              <div className="relative py-3 sm:py-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 py-1 text-gray-800 bg-emerald-200 rounded-full">
                    or
                  </span>
                </div>
              </div>

              {/* Back to Login link — long-arrow icon + underlined text (only) */}
              <div className="text-center">
                <Link
                  to="/signin"
                  className="inline-flex items-center justify-center gap-1.5 text-[15px] font-medium text-gray-800 hover:text-gray-600"
                >
                  <svg width="20" height="13" viewBox="0 0 24 15" fill="none" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M23.0526 6.27387C23.5758 6.27387 24 6.6952 24 7.21495C24 7.73469 23.5758 8.15603 23.0526 8.15603L3.23452 8.15603L7.93305 12.8234C8.30302 13.1909 8.30302 13.7867 7.93305 14.1543C7.56308 14.5218 6.96324 14.5218 6.59327 14.1543L0.277477 7.88039C-0.0924931 7.51288 -0.0924931 6.91702 0.277477 6.5495L6.59327 0.275636C6.96324 -0.0918786 7.56308 -0.0918786 7.93305 0.275636C8.30302 0.643151 8.30302 1.23901 7.93305 1.60652L3.23452 6.27387L23.0526 6.27387Z" fill="currentColor"/>
                  </svg>
                  <span className="underline">Back to Login</span>
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
